/** Bar inside a client reached through a master account: the master account, a client switcher and "back to the board". */
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Select, type SelectInputOption } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useAccountRelationName, useOwnBooksLabel } from '@/hooks/use-account-relation-name';
import { useAuth } from '../../context';
import { useReturnToMasterAccountBoard } from '../../hooks/use-return-to-master-account-board';
import { useSwitchAccount } from '../../hooks/use-switch-account';
import { clientSwitchTarget } from '../../utils/client-switch-target';
import { accountRelationsOf, searchAccountRelations } from '../../utils/account-relations';

/** Rows rendered at once; a master account with more clients narrows the list by searching. */
export const MASTER_ACCOUNT_BAR_VISIBLE_CLIENTS = 50;

/** Value of the disabled row that tells a capped list has more matches. */
const MORE_MATCHES_VALUE = '__master_account-bar-more__';

export function MasterAccountBar() {
  const t = useFeatureTranslation('auth.masterAccountBar');
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { masterAccount, returnToMasterAccountBoard, returning } = useReturnToMasterAccountBoard();
  const { switchAccount, switching, activeAccountId } = useSwitchAccount();
  const [query, setQuery] = useState('');
  // Remounts the switcher once a switch settles: a refused one must not leave it on the client
  // that was picked but never entered.
  const [switchAttempt, setSwitchAttempt] = useState(0);

  const clientName = useAccountRelationName();
  const ownBooksLabel = useOwnBooksLabel();
  const masterAccountId = masterAccount?.id ?? null;
  const clients = useMemo(
    () =>
      masterAccountId
        ? accountRelationsOf(user?.memberships, masterAccountId, i18n.language, ownBooksLabel)
        : [],
    [user?.memberships, masterAccountId, i18n.language, ownBooksLabel]
  );

  const { visible, total } = useMemo(
    () => searchAccountRelations(clients, query, MASTER_ACCOUNT_BAR_VISIBLE_CLIENTS),
    [clients, query]
  );

  const moreMatchesLabel =
    total > visible.length ? t('moreMatches', { shown: visible.length, total }) : null;

  // Stable between renders: the Select re-derives its selection whenever `options` changes.
  const options = useMemo<SelectInputOption[]>(() => {
    const rows: SelectInputOption[] = visible.map(c => ({ value: c.id, label: c.name }));
    if (moreMatchesLabel) {
      rows.push({ value: MORE_MATCHES_VALUE, label: moreMatchesLabel, disabled: true });
    }
    return rows;
  }, [visible, moreMatchesLabel]);

  if (!masterAccount) {
    return null;
  }

  const busy = switching || returning;

  const openClient = (value: string | number | (string | number)[]) => {
    if (typeof value !== 'string' || value === activeAccountId || value === MORE_MATCHES_VALUE) {
      return;
    }
    // A client→client switch starts in a client session, so the saved board URL survives it.
    void switchAccount(value, clientSwitchTarget(location.pathname), { keepsPlace: true }).then(
      () => {
        // The remounted switcher starts with an empty search field, so the list must too.
        setQuery('');
        setSwitchAttempt(n => n + 1);
      }
    );
  };

  return (
    <nav
      aria-label={t('label')}
      data-testid='master-account-bar'
      className='flex flex-wrap items-center justify-between gap-sm bg-info-bg border-b border-info-border px-md py-xs'
    >
      <div className='flex min-w-0 flex-wrap items-center gap-sm'>
        <Button
          variant='ghost'
          size='sm'
          icon='briefcase'
          onClick={returnToMasterAccountBoard}
          disabled={busy}
          testId='master-account-bar-master-account-btn'
          aria-label={t('masterAccount', { name: masterAccount.name })}
          title={masterAccount.name}
          // The label span is the button's last child; it truncates instead of widening the bar.
          className='-ml-md max-w-48 font-semibold text-info-text lg:max-w-96 [&>span:last-child]:min-w-0 [&>span:last-child]:truncate'
        >
          {masterAccount.name}
        </Button>
        {/* Only a separator while master account and client share a line; stacked, it would dangle. */}
        <span aria-hidden='true' className='hidden text-info-text sm:inline'>
          ›
        </span>
        <div className='w-72 max-w-full'>
          <Select
            key={switchAttempt}
            isSearchable
            filtersExternally
            options={options}
            value={activeAccountId}
            // Shown while the active client is outside the searched or capped list.
            buttonLabel={
              user?.account
                ? clientName(user.account.name, user.account.isMasterAccountOwnBooks)
                : undefined
            }
            onSearch={setQuery}
            onChange={openClient}
            disabled={busy}
            height='sm'
            ariaLabel={t('switchClient')}
            testId='master-account-bar-client-select'
          />
        </div>
      </div>
      <Button
        variant='ghost'
        size='sm'
        icon='arrow-left'
        onClick={returnToMasterAccountBoard}
        disabled={busy}
        testId='master-account-bar-back-btn'
        className='-mx-md text-info-text'
      >
        {t('backToMasterAccountBoard')}
      </Button>
    </nav>
  );
}
